// The word guard on a walkthrough's clicks (design/2026-10-20-walkthroughs.md).
// A walkthrough shows the page; it never saves, sends, deletes or pays for
// the person. A click whose label says it commits something runs nothing and
// the card says why.
//
// The lists and the rule are FleetView's browser walk (fleetd/walkcommit.go
// `walkCommitWords`, `walkCommitSafe`, plus `sceneCommitWords`), matched the
// same way (lower case, anywhere in the label), so one walkthrough file
// behaves the same in both runners. Platinum's own CONFIRM_LABEL (the screen
// scan's list of labels that change or send something) is checked next, then
// PLATINUM_COMMIT_LABEL, Platinum's controls that write, download, leave or
// spend AI credits.

import { CONFIRM_LABEL } from '../voice/screenControls'

/** Words that mean "this does something in the world". `post ` keeps its space so "poster" passes. */
export const COMMIT_WORDS = [
  'pay', 'payment', 'checkout', 'purchase', 'buy', 'order',
  'confirm', 'submit', 'book', 'reserve', 'subscribe',
  'delete', 'remove', 'destroy', 'erase', 'wipe',
  'send', 'publish', 'post ', 'share', 'invite',
  'transfer', 'withdraw', 'refund', 'charge',
  'approve', 'accept', 'agree', 'sign',
  'deactivate', 'disable', 'archive', 'merge', 'deploy', 'release',
  'save',
] as const

/**
 * Platinum's own labels that write, download, sign out or spend AI credits,
 * as whole words ("Post" but not "Poster", "Star" but not "Start"). `apply`
 * is left out on purpose: Quick Filter's Apply only changes the view, and a
 * demo may want it (walkthroughs validator, 20 Oct).
 */
export const PLATINUM_COMMIT_LABEL =
  /\b(post|insights|log out|download|export|duplicate|star|make a table|test|distil|generate|install|upload|import|refresh|rename|move)\b/i

/** Labels that contain a commit word but back out of something. They win over the rest. */
export const SAFE_WORDS = [
  'cancel', 'close', 'back', 'dismiss', 'not now', 'no thanks',
  'go back', 'return', 'previous', 'undo', 'skip',
] as const

export type ClickCheck = { ok: true } | { ok: false; word: string }

// Safe words match whole words, so "Send feedback" and "Delete backup" are
// refused: as substrings, "back" in them waved the commit word through
// (FleetView walkthrough card validator, 20 Oct). fleetd's browser walk still
// matches substrings.
const SAFE_LABEL = new RegExp(`\\b(${SAFE_WORDS.join('|')})\\b`)

/**
 * Platinum's step buttons that only change the view: the Theme AI task
 * stepper ("Run step: Done", StageStepper). "Run" alone is a commit word, so
 * without this a walkthrough could not move from Results to the Run step.
 */
export const PLATINUM_NAV_LABEL = /^(data|code frame|run|results) step\b/i

/** Whether a walkthrough may click the control with this label, and the word that refused it. */
export function clickIsSafe(label: string): ClickCheck {
  const hay = label.toLowerCase()
  if (SAFE_LABEL.test(hay) || PLATINUM_NAV_LABEL.test(label.trim())) return { ok: true }
  const word = COMMIT_WORDS.find((w) => hay.includes(w))
  if (word) return { ok: false, word: word.trim() }
  const confirm = CONFIRM_LABEL.exec(label) ?? PLATINUM_COMMIT_LABEL.exec(label)
  if (confirm) return { ok: false, word: confirm[1].toLowerCase() }
  return { ok: true }
}
